import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import DataTables from 'material-ui-datatables';
import SelectField from 'material-ui/SelectField';
import FontIcon from 'material-ui/FontIcon';
import IconButton from 'material-ui/IconButton';
import MenuItem from 'material-ui/MenuItem';

import ResourceInput from '../ResourceInput';
import { hasPermission } from '../../utils/authenticated';
import { sortStrings, sortDates, getOrderDirection, contains, id } from '../../utils/helper';

import commonStyles from '../styles';
import styles from './styles';

const getTableColumns = (employees, editable, onSelectEmployee, onRemove) => {
  return _.flatten([
    {
      key: 'role',
      label: 'Role',
      render: (role) => role ? role.name : 'Unknown',
      sortable: true,
      style: styles.roleCol,
    }, {
      key: 'date',
      label: 'Date',
      render: (start, resource) => resource.from ? moment(resource.from).format('ll') : '',
      sortable: true,
      style: styles.dateCol,
    }, {
      key: 'from',
      label: 'Start',
      render: (from) => from ? moment(from).format('LT') : '',
      sortable: true,
      style: styles.timeCol,
    }, {
      key: 'to',
      label: 'End',
      render: (to) => to ? moment(to).format('LT') : '',
      sortable: true,
      style: styles.timeCol,
    }, {
      key: 'employee',
      label: 'Employee',
      render: (employee, resource) => {
        // const employee = employeeId ? employees.find((e) => e.id === employeeId) || { name: 'Unknown' } : { name: '' };
        return (<SelectField
          name="employeeId"
          style={commonStyles.input}
          hintText="Employee"
          fullWidth
          readOnly={!editable}
          value={employee ? employee.id : null}
          onChange={(evt, idx, eId) => onSelectEmployee(resource, eId)}
        >
          { (employees || []).map((e) => <MenuItem key={e.id} value={e.id} primaryText={e.name}/>)}
        </SelectField>);
      },
      style: styles.employeeCol,
    }, {
      key: 'action',
      label: '',
      render: (a, resource) => {
        return (<span>
{ editable ? <IconButton
  tooltip="Clear"
  tooltipPosition="bottom-center"
  style={styles.clearEmployee}
  onClick={() => onSelectEmployee(resource, null)}
>
  <FontIcon className="material-icons" style={styles.clearEmployee}>clear</FontIcon>
</IconButton> : null }
          { editable && onRemove ? <IconButton
            tooltip="Remove"
            tooltipPosition="bottom-center"
            style={styles.removeDate}
            onClick={() => onRemove(resource)}>
            <FontIcon className="material-icons" style={styles.removeResource}>delete</FontIcon>
          </IconButton> : null }
           </span>);
      },
      alignRight: true,
      style: styles.actionCol,
    },
  ]);
};

const getSorter = (sorter = (a, b) => 0, order = 'asc') => {
  return (a, b) => {
    let result = sorter(a, b) * getOrderDirection(order);

    if (result === 0) {
      result = sortDates(a.from, b.from);

      if (result === 0) {
        result = sortDates(a.to, b.to);

        if (result === 0) {
          result = sortStrings(a.id, b.id);
        }
      }
    }

    return result;
  };
};

const getFilter = (filter) => {
  return (resource) => {
    if (filter) {
      if (resource.role.name && contains(resource.role.name, filter)) {
        return true;
      }

      return false;
    }

    return true;
  };
};

class ResourcesTable extends Component {
  static propTypes = {
    resources: PropTypes.array.isRequired,
    employees: PropTypes.array.isRequired,
    // onEdit: PropTypes.func.isRequired,
    // onRemove: PropTypes.func,
    onChange: PropTypes.func.isRequired,
  };

  state = {
    selectedRows: [],
    page: 1,
    limit: 5,
    sortBy: getSorter((a, b) => sortDates(a.from, b.from), 'asc'),
    order: 'asc',
    data: [],
  };

  componentWillMount() {
    this.setState({
      data: this.props.resources.sort(this.state.sortBy),
    });
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      data: nextProps.resources.sort(this.state.sortBy),
    });
  }

  handleAddResources = (spec) => {
    const { resources, onChange } = this.props;
    const newResources = _.range(spec.quantity).map(() => _.assign(_.omit(spec, ['quantity']), { id: id() }));
    return onChange(_.concat(resources, newResources));
  }

  handleSelectEmployee = (resource, employeeId) => {
    const { resources, onChange } = this.props;

    if (employeeId) {
      const employee = this.props.employees.find((e) => e.id === employeeId);
      return onChange(resources.map((r) => {
        if (r.id === resource.id) {
          return _.assign(resource, { employee });
        }

        return r;
      }));
    } else {
      return onChange(resources.map((r) => {
        if (r.id === resource.id) {
          return _.omit(resource, ['employee','employeeId']);
        }

        return r;
      }));
    }
  };

  handleRemove = (resource) => {
    const { resources, onChange } = this.props;
    onChange(resources.filter((r) => r.id !== resource.id));
  }

  handleFilterValueChange = (filter) => {
    const { resources } = this.props;
    const { page, limit, sortBy } = this.state;

    const data = resources.filter(getFilter(filter ? filter.trim().toLowerCase() : '')).sort(sortBy);
    this.setState({
      page: Math.min(page, Math.floor(data.length / limit) + 1),
      data,
    });
  };

  handleSortOrderChange = (key, order) => {
    let sortBy = getSorter();
    switch (key) {
      case 'role':
        sortBy = getSorter((a, b) => sortStrings(a.role ? a.role.name : '', b.role ? b.role.name : ''), order);
        break;
      case 'date':
      case 'from':
        sortBy = getSorter((a, b) => sortDates(a.from, b.from), order);
        break;
      case 'to':
        sortBy = getSorter((a, b) => sortDates(a.to, b.to), order);
        break;
      default:
        break;
    }

    this.setState({
      data: this.state.data.sort(sortBy),
      sortBy,
    });
  };

  handlePreviousPageClick = () => {
    this.setState({
      page: Math.max(1, this.state.page - 1),
    });
  };

  handleNextPageClick = () => {
    const { data, page, limit } = this.state;

    this.setState({
      page: Math.min(Math.ceil(data.length / limit), page + 1),
    });
  };

  handleRowSizeChange = (a, limit) => {
    this.setState({
      limit,
      page: 1,
    });
  };

  renderNoData() {
    return (<div style={styles.noDataRow}>
      <FontIcon className="material-icons" style={styles.noData}>person</FontIcon>
      <div>No resources currently assigned</div>
    </div>);
  }

  render() {
    const { employees, editable } = this.props;
    const { data, limit, page } = this.state;

    if (!data || data.length === 0) {
      return this.renderNoData();
    }

    return (<div>
      { editable ? <ResourceInput onAdd={this.handleAddResources}/> : null }
      <DataTables
        height={'auto'}
        showRowHover
        columns={getTableColumns(employees, editable, this.handleSelectEmployee, this.handleRemove)}
        data={data.slice((page - 1) * limit, page * limit)}
        onFilterValueChange={this.handleFilterValueChange}
        onSortOrderChange={this.handleSortOrderChange}
        onPreviousPageClick={this.handlePreviousPageClick}
        onNextPageClick={this.handleNextPageClick}
        onRowSizeChange={this.handleRowSizeChange}
        page={page}
        rowSize={limit}
        rowSizeList={[5, 10, 15, 25, 50]}
        count={data.length}
        filterHintText="Filter"
        headerToolbarMode="filter"
        showHeaderToolbar
      />
    </div>);
  }
}

export default ResourcesTable;



// WEBPACK FOOTER //
// ./src/components/ResourcesTable/index.js